import React from 'react';
import { useResizableColumns, ResizeHandle, ColGroup } from '../hooks/useResizableColumns';
import { SelectCheckbox, BulkStatusBar } from './InlineEditTable';

/**
 * DataTable — shared scaffold for the inline-editable data tables on Programs
 * (list), Components, Goals, Invoices. Owns resizable columns, the header
 * (with optional sorting), an optional trailing bulk-select column +
 * BulkStatusBar, optional expandable sub-rows, and the empty state. The page
 * supplies the column config + cell renderers and keeps all its own state.
 *
 * Props:
 *   rows           — array of row objects
 *   rowKey         — (row) => unique key/id            (default: row.id)
 *   columns        — [{ key, header, width, render?(row), sortable?, align?, vAlign?,
 *                        footer?(rows), frozen? }] — `frozen` on a leading run of
 *                    columns keeps them in place while the rest scroll sideways
 *                    `render` returns the cell content (default: row[key]).
 *                    `width` feeds the resizable colgroup.
 *                    `footer` opts the table into a totals row: when ANY column
 *                    defines one, a <tfoot> is rendered and each column shows
 *                    its own footer(rows) (blank for columns without one). Kept
 *                    per-column rather than as a page-supplied row so the
 *                    footer can never drift out of alignment with the headers.
 *   sort           — optional { field, dir, onSort(key) }; sortable columns
 *                    become clickable and show a ▲/▼ indicator.
 *   bulk           — optional { selected:Set, toggle(id), toggleAll(ids),
 *                    statuses, onApply(status), enabled? }. When present, adds a
 *                    trailing "Bulk Status Update" column (select-all in header,
 *                    checkbox per row) and renders a BulkStatusBar above the
 *                    table while rows are selected. `enabled === false` keeps the
 *                    column for layout but hides the checkboxes (e.g. read-only
 *                    users on Invoices).
 *   renderExpanded — optional (row) => node|null; when it returns a node, it's
 *                    rendered as a full-width sub-row beneath that row.
 *   onRowClick     — optional (row) => void. Makes rows clickable, with a
 *                    pointer cursor and hover highlight. Cells that contain
 *                    their own controls should stopPropagation.
 *   rowStyle       — optional (row) => style for the main <tr>.
 *   emptyText      — shown when rows is empty.
 *   dense          — optional; tighter padding and 12px text (fits more rows on screen).
 */
export default function DataTable({
  rows, rowKey = r => r.id, columns, sort, bulk,
  renderExpanded, rowStyle, onRowClick, emptyText = 'No rows found.', dense = false,
}) {
  const hasFooter = columns.some(c => c.footer);
  const hasBulk = !!bulk;
  const bulkEnabled = hasBulk && bulk.enabled !== false;

  // Column widths: page-defined columns + a trailing bulk column when present.
  const widthList = columns.map(c => c.width || 120);
  if (hasBulk) widthList.push(110);
  const { widths, startResize, tableWidth } = useResizableColumns(widthList);

  const totalCols = columns.length + (hasBulk ? 1 : 0);
  const visibleIds = rows.map(rowKey);
  const allSelected = hasBulk && visibleIds.length > 0 && visibleIds.every(id => bulk.selected.has(id));

  // `dense`: tighter padding and 12px text, for a table meant to show as many
  // rows as fit on one screen (Programs' Compact view, 2026-11-04).
  const thStyle = {
    padding: dense ? '6px 8px' : '10px', textAlign: 'left', ...(dense ? { fontSize: '12px' } : {}), borderBottom: '2px solid #ddd',
    position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap', background: '#f5f5f5',
    userSelect: 'none',
  };
  const tdStyle = { padding: dense ? '4px 8px' : '10px', borderBottom: '1px solid #eee', fontSize: dense ? '12px' : '13px' };

  // Frozen columns (`frozen: true` on a LEADING run of columns) stay put while
  // the rest scroll sideways under them. Each gets `position: sticky` at the sum
  // of the widths before it, its own background (a sticky cell is see-through
  // otherwise), and the last one a soft edge. Rows get a white default so a
  // frozen cell's `inherit` has something to inherit, hover included.
  const frozenCount = columns.findIndex(c => !c.frozen) === -1 ? columns.length : columns.findIndex(c => !c.frozen);
  const hasFrozen = frozenCount > 0;
  const frozenStyle = (i, bg) => {
    if (i >= frozenCount) return {};
    const left = widths.slice(0, i).reduce((n, w) => n + w, 0);
    return {
      position: 'sticky', left, zIndex: bg === 'inherit' ? 1 : 2, background: bg,
      ...(i === frozenCount - 1 ? { boxShadow: 'inset -1px 0 0 #dde3e9, 4px 0 6px -4px rgba(0,0,0,0.18)' } : {}),
    };
  };
  const restingRowBg = (row) => (rowStyle ? rowStyle(row).background : undefined) || (hasFrozen ? 'white' : '');

  const arrow = (key) => (sort && sort.field === key) ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <>
      {bulkEnabled && (
        <BulkStatusBar count={bulk.selected.size} statuses={bulk.statuses} onApply={bulk.onApply}
          onClear={() => bulk.toggleAll([])} />
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: tableWidth }}>
          <ColGroup widths={widths} />
          <thead>
            <tr>
              {columns.map((c, i) => {
                const canSort = sort && c.sortable;
                return (
                  <th key={c.key}
                    onClick={canSort ? () => sort.onSort(c.key) : undefined}
                    style={{ ...thStyle, cursor: canSort ? 'pointer' : 'default', textAlign: c.align || 'left', ...frozenStyle(i, thStyle.background) }}>
                    {c.header}{canSort ? arrow(c.key) : ''}
                    <ResizeHandle onMouseDown={e => { e.stopPropagation(); startResize(i, e); }} />
                  </th>
                );
              })}
              {hasBulk && (
                <th style={{ ...thStyle, cursor: 'default', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal', whiteSpace: 'normal', lineHeight: '1.2', textAlign: 'center' }}>Bulk Status Update</span>
                    {bulkEnabled && <SelectCheckbox checked={allSelected} onChange={() => bulk.toggleAll(visibleIds)} />}
                  </div>
                  <ResizeHandle onMouseDown={e => startResize(columns.length, e)} />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const id = rowKey(row);
              const expanded = renderExpanded ? renderExpanded(row) : null;
              return (
                <React.Fragment key={id}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onMouseEnter={onRowClick ? e => { e.currentTarget.style.background = '#f8f9fa'; } : undefined}
                    onMouseLeave={onRowClick ? e => { e.currentTarget.style.background = restingRowBg(row); } : undefined}
                    style={{ ...(hasFrozen ? { background: 'white' } : {}), ...(rowStyle ? rowStyle(row) : {}), ...(onRowClick ? { cursor: 'pointer' } : {}) }}>
                    {columns.map((c, i) => (
                      <td key={c.key} style={{ ...tdStyle, textAlign: c.align || 'left', whiteSpace: c.nowrap ? 'nowrap' : undefined, verticalAlign: c.vAlign, ...frozenStyle(i, 'inherit') }}>
                        {c.render ? c.render(row) : row[c.key]}
                      </td>
                    ))}
                    {hasBulk && (
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {bulkEnabled && <SelectCheckbox checked={bulk.selected.has(id)} onChange={() => bulk.toggle(id)} />}
                      </td>
                    )}
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={totalCols} style={{ padding: 0 }}>{expanded}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={totalCols} style={{ ...tdStyle, textAlign: 'center', color: '#aaa', padding: '40px' }}>{emptyText}</td></tr>
            )}
          </tbody>
          {hasFooter && rows.length > 0 && (
            <tfoot>
              <tr style={{ background: '#eef2f6' }}>
                {columns.map((c, i) => (
                  <td key={c.key} style={{ ...tdStyle, borderTop: '2px solid #d5dde8', borderBottom: 'none', fontWeight: '600', color: '#2c3e50', textAlign: c.align || 'left', ...frozenStyle(i, '#eef2f6') }}>
                    {c.footer ? c.footer(rows) : null}
                  </td>
                ))}
                {hasBulk && <td style={{ ...tdStyle, borderTop: '2px solid #d5dde8', borderBottom: 'none' }} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}
